import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

export interface PdfViewSource {
  /** URL the iframe renders — a blob object-URL or a pre-signed remote URL. */
  src: string;
  /** Optional teardown for the src (e.g. URL.revokeObjectURL for blob URLs). */
  cleanup?: () => void;
}

interface ReadonlyPdfViewProps {
  /**
   * Resolve the PDF to display. Return `null` when there is no document.
   * The view re-loads whenever this callback's identity changes, so callers
   * should wrap it in `useCallback` with the values that should trigger a
   * refresh (task id, last-updated token, …).
   */
  loadSource: () => Promise<PdfViewSource | null>;
  title?: string;
  errorText?: string;
}

/**
 * Shared read-only PDF preview: fetches a source, renders it in an iframe, and
 * shows spinner / error states. Used wherever a signed or locked task is shown
 * as a document (visit chart, summary orders, 30-Day Summary). The only thing
 * that varies per caller is how the source is resolved, so that is the single
 * prop — everything else (loading, error, object-URL cleanup) lives here.
 */
export const ReadonlyPdfView: React.FC<ReadonlyPdfViewProps> = ({
  loadSource,
  title = 'PDF Preview',
  errorText = 'Failed to load preview.',
}) => {
  const [src, setSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let cleanup: (() => void) | undefined;
    setIsLoading(true);
    setHasError(false);
    setSrc(null);

    loadSource()
      .then(result => {
        if (cancelled) {
          result?.cleanup?.();
          return;
        }
        if (!result) {
          setHasError(true);
          return;
        }
        cleanup = result.cleanup;
        setSrc(result.src);
      })
      .catch((err: unknown) => {
        console.error('Failed to load PDF preview:', err);
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [loadSource]);

  if (isLoading) {
    return (
      <div className='h-full w-full flex items-center justify-center border rounded-md'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  if (hasError || !src) {
    return (
      <div className='h-full w-full flex items-center justify-center border rounded-md text-muted-foreground'>
        <p>{errorText}</p>
      </div>
    );
  }

  return (
    <div className='h-full w-full border rounded-md overflow-hidden bg-muted/10'>
      <iframe src={src} title={title} className='h-full w-full' />
    </div>
  );
};
